import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Building2,
  Users,
  ArrowRight,
  Shield,
  Target,
  Handshake,
  CheckCircle2,
  Phone,
  Mail,
  Linkedin,
  MapPin,
  Lock,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import UserDashboard from "@/components/UserDashboard";

const ProfileCard = ({ name, age, profile, project, capabilities, vision, enterprise, parcours, objective, priority }) => (
  <div className="bg-secondary p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
    <h3 className="text-2xl font-bold text-primary mb-4">{name} - {age}</h3>
    <div className="space-y-2 text-foreground">
      {profile && <p><span className="font-semibold">Profil :</span> {profile}</p>}
      {enterprise && <p><span className="font-semibold">Entreprise :</span> {enterprise}</p>}
      {project && <p><span className="font-semibold">Projet :</span> {project}</p>}
      {parcours && <p><span className="font-semibold">Parcours :</span> {parcours}</p>}
      {capabilities && <p><span className="font-semibold">Capacités :</span> {capabilities}</p>}
      {objective && <p><span className="font-semibold">Objectif :</span> {objective}</p>}
      {vision && <p><span className="font-semibold">Vision :</span> {vision}</p>}
      {priority && <p><span className="font-semibold">Priorité :</span> {priority}</p>}
    </div>
    <div className="mt-6">
      <span className="text-accent font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
        Découvrir le profil <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </div>
);

export default function LandingPage() {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCA = (value) => {
    if (!value) return "-";
    if (typeof value === "string" && value.includes("€")) {
        return value;
    }
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return formatCurrency(num);
  };

  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setIsLoggedIn(!!token);
    const fetchOpportunities = async () => {
      try {
        const response = await axios.get(`${API}/opportunites`);
        setOpportunities(response.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching opportunities:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunities();
  }, []);

  if (isLoggedIn) {
    return (
      <div className="min-h-screen bg-background pt-24" data-testid="landing-page-logged-in">
        <UserDashboard />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight text-primary mb-8">
            Les plus belles transmissions commencent
            <br />
            <span className="text-accent">par une première rencontre.</span>
          </h1>
          <p className="text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed mb-12">
            Que vous cherchiez à transmettre votre entreprise ou à reprendre
            un projet, Sequenti prépare, structure et sécurise chaque étape de
            la transmission, de la première rencontre jusqu'au closing.
          </p>
        </div>

        {/* Split Cards */}
        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto mt-16">
          {/* Cédant Card */}
          <div
            className="group relative bg-muted border-none shadow-sm hover:shadow-md transition-all duration-300 rounded-lg p-10 cursor-pointer overflow-hidden"
            onClick={() => navigate("/cedant")}
            data-testid="cedant-card"
          >
            <div className="relative z-10">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-8">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-semibold text-primary mb-6">
                Je suis cédant
              </h2>
              <p className="text-foreground/70 mb-8 leading-relaxed text-lg">
                Vous avez bâti une entreprise et cherchez un successeur aligné
                à votre ADN. Découvrez la valeur de votre entreprise et
                trouvez le bon repreneur.
              </p>
              <ul className="space-y-4 mb-10 text-foreground/60">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  Estimation de valorisation gratuite
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  Accompagnement sur 6 mois
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />1
                  repreneur qualifié / semaine
                </li>
              </ul>
              <Button
                className="w-full bg-accent text-white hover:bg-accent/90 rounded-md py-6 text-lg shadow-sm transition-all duration-200"
                data-testid="cedant-cta"
              >
                Estimez votre entreprise
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Repreneur Card */}
          <div
            className="group relative bg-muted border-none shadow-sm hover:shadow-md transition-all duration-300 rounded-lg p-10 cursor-pointer overflow-hidden"
            onClick={() => navigate("/repreneur")}
            data-testid="repreneur-card"
          >
            <div className="relative z-10">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-8">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-semibold text-primary mb-6">
                Je suis repreneur
              </h2>
              <p className="text-foreground/70 mb-8 leading-relaxed text-lg">
                Vous cherchez une entreprise alignée à votre projet et vos
                valeurs. Évaluez votre profil et accédez aux opportunités
                qualifiées.
              </p>
              <ul className="space-y-4 mb-10 text-foreground/60">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  Questionnaire de qualification
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  Accès aux opportunités selon profil
                </li> 
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-accent rounded-full" />
                  Catalogue des opportunités 
                </li>
              </ul>
              <Button
                className="w-full bg-primary text-white hover:bg-primary/90 rounded-md py-6 text-lg shadow-sm transition-all duration-200"
                data-testid="repreneur-cta"
              >
                Évaluez votre profil
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-24 px-8 bg-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-primary mb-6">Une question sur votre reprise / transmission?</h2>
          <p className="text-xl text-foreground/80 mb-12">
            Que vous soyez prêt·e aujourd'hui ou dans 6 mois, parlons-en. Éric prend le temps d'écouter votre situation avant toute proposition.
          </p>
          <Button
            className="bg-accent hover:bg-accent/90 text-white px-10 py-8 text-xl rounded-md shadow-lg"
            onClick={() => window.open('https://calendly.com/eric-sequenti/30min', '_blank')}
          >
            Prendre rendez-vous
          </Button>
          <div className="mt-12 flex flex-col sm:flex-row justify-center gap-8 text-primary">
            <a href="tel:0757810151" className="flex items-center justify-center gap-2 font-medium hover:underline">
              <Phone className="w-5 h-5" /> 07 57 81 01 51
            </a>
            <a href="mailto:eric@sequenti.fr" className="flex items-center justify-center gap-2 font-medium hover:underline">
              <Mail className="w-5 h-5" /> eric@sequenti.fr
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-muted border-t border-border/50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <p className="text-3xl font-bold text-primary">
              Sequenti
            </p>
            <p className="text-foreground/60 text-lg">
              AURA • PACA • Occitanie
            </p>
            <div className="flex gap-6">
              <a href="https://www.linkedin.com/in/sequenti-transmission-878b82258/" target="_blank" rel="noreferrer" className="text-primary hover:text-accent">
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap justify-center gap-8 text-sm text-foreground/40">
            <a href="/mentions-legales" className="hover:underline">Mentions légales</a>
            <a href="/politique-confidentialite" className="hover:underline">Politique de confidentialité</a>
            <a href="/cookies" className="hover:underline">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}